import { Form } from '@inertiajs/react'
import { useMemo, useState } from 'react'

import { Combobox } from '@/components/ui/combobox'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'

type AvailableRepository = {
  fullName: string
  name: string
  owner: string
  description?: string | null
}

type SetupFormProps = {
  action: string
  submitLabel: string
  selectedFullNames: string[]
  availableRepositories: AvailableRepository[]
  digestFrequency: string
  timezone: string
  digestHour: number
  emailDigestEnabled: boolean
  slackNotificationHour: number
  weeklyLeaderboardEnabled: boolean
  weeklyLeaderboardHour: number
  isSlackConnected: boolean
  githubError?: string | null
}

function TimeField({
  id,
  name,
  label,
  value,
  onChange,
  disabled,
}: {
  id: string
  name: string
  label: string
  value: number
  onChange: (hour: number) => void
  disabled?: boolean
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="time"
        value={`${value.toString().padStart(2, '0')}:00`}
        onChange={(event) => {
          const [hours] = event.target.value.split(':').map(Number)
          onChange(Number.isFinite(hours) ? hours : 0)
        }}
        disabled={disabled}
      />
      <input type="hidden" name={name} value={String(value)} />
    </div>
  )
}

function SettingsFormSection({
  title,
  description,
  children,
}: {
  title: string
  description: string
  children: React.ReactNode
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  )
}

function ChannelSubsection({
  title,
  description,
  children,
}: {
  title: string
  description: string
  children: React.ReactNode
}) {
  return (
    <div className="bg-muted/40 space-y-4 rounded-lg border p-4">
      <div className="space-y-1">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-muted-foreground text-xs">{description}</p>
      </div>
      {children}
    </div>
  )
}

export function SetupForm({
  action,
  submitLabel,
  selectedFullNames,
  availableRepositories,
  digestFrequency: initialFrequency,
  timezone: initialTimezone,
  digestHour: initialDigestHour,
  emailDigestEnabled: initialEmailDigestEnabled,
  slackNotificationHour: initialSlackHour,
  weeklyLeaderboardEnabled: initialWeeklyLeaderboardEnabled,
  weeklyLeaderboardHour: initialWeeklyLeaderboardHour,
  isSlackConnected,
  githubError,
}: SetupFormProps) {
  const [selectedRepos, setSelectedRepos] = useState<string[]>(selectedFullNames)
  const [timezone, setTimezone] = useState(initialTimezone)
  const [digestFrequency, setDigestFrequency] = useState(initialFrequency || 'daily')
  const [digestHour, setDigestHour] = useState(initialDigestHour)
  const [slackNotificationHour, setSlackNotificationHour] = useState(initialSlackHour)
  const [emailDigestEnabled, setEmailDigestEnabled] = useState(initialEmailDigestEnabled)
  const [weeklyLeaderboardEnabled, setWeeklyLeaderboardEnabled] = useState(initialWeeklyLeaderboardEnabled)
  const [weeklyLeaderboardHour, setWeeklyLeaderboardHour] = useState(initialWeeklyLeaderboardHour)

  const repositoryOptions = useMemo(
    () =>
      availableRepositories.map((repo) => ({
        value: repo.fullName,
        label: repo.fullName,
      })),
    [availableRepositories],
  )

  const timezoneOptions = useMemo(() => {
    const intlWithZones = Intl as typeof Intl & { supportedValuesOf?: (key: 'timeZone') => string[] }
    const zones = intlWithZones.supportedValuesOf?.('timeZone') ?? [initialTimezone || 'UTC']
    return zones.map((zone: string) => ({ value: zone, label: zone }))
  }, [initialTimezone])

  return (
    <Form method="patch" action={action}>
      {({ errors, processing }) => (
        <div className="space-y-6">
          {githubError ? (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{githubError}</p>
          ) : null}

          <SettingsFormSection
            title="Repositories"
            description="Pick which GitHub repositories should be digested for this workspace."
          >
            {availableRepositories.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                No repositories found on GitHub for your account. Make sure the GitHub OAuth app has access.
              </p>
            ) : (
              <div className="space-y-2">
                <Label>Tracked repositories</Label>
                <Combobox
                  multiple
                  name="repositories[]"
                  options={repositoryOptions}
                  value={selectedRepos}
                  onChange={(value) => setSelectedRepos(value as string[])}
                  placeholder="Select repositories…"
                  searchPlaceholder="Search repositories…"
                  emptyText="No repositories found."
                />
                {errors.repositories ? <p className="text-sm text-red-600">{errors.repositories}</p> : null}
              </div>
            )}
          </SettingsFormSection>

          <SettingsFormSection
            title="Digest delivery"
            description="Choose when and how often digests go out for this workspace."
          >
            <div className="space-y-2">
              <Label>Organization timezone</Label>
              <Combobox
                name="timezone"
                options={timezoneOptions}
                value={timezone}
                onChange={(value) => setTimezone(value as string)}
                placeholder="Select timezone…"
                searchPlaceholder="Search timezones…"
              />
            </div>

            <div className="space-y-3">
              <Label>Digest schedule</Label>
              <RadioGroup
                value={digestFrequency}
                onValueChange={setDigestFrequency}
                className="grid gap-3 sm:grid-cols-2"
              >
                <label
                  className={cn(
                    'hover:bg-muted/50 flex cursor-pointer items-start gap-3 rounded-lg border p-3',
                    digestFrequency === 'daily' && 'border-primary bg-muted/40',
                  )}
                >
                  <RadioGroupItem value="daily" id="freq-daily" />
                  <div>
                    <p className="text-sm font-medium">Daily</p>
                    <p className="text-muted-foreground text-xs">A digest every weekday in your timezone.</p>
                  </div>
                </label>
                <label
                  className={cn(
                    'hover:bg-muted/50 flex cursor-pointer items-start gap-3 rounded-lg border p-3',
                    digestFrequency === 'weekly' && 'border-primary bg-muted/40',
                  )}
                >
                  <RadioGroupItem value="weekly" id="freq-weekly" />
                  <div>
                    <p className="text-sm font-medium">Weekly</p>
                    <p className="text-muted-foreground text-xs">One digest covering the past week.</p>
                  </div>
                </label>
              </RadioGroup>
              <input type="hidden" name="digestFrequency" value={digestFrequency} />
            </div>

            <ChannelSubsection
              title="Email digest"
              description="Send a markdown digest to workspace members by email."
            >
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="emailDigestEnabled">Send code digest by email</Label>
                <div className="flex items-center gap-2">
                  <Switch
                    id="emailDigestEnabled"
                    checked={emailDigestEnabled}
                    onCheckedChange={setEmailDigestEnabled}
                  />
                  <input type="hidden" name="emailDigestEnabled" value={emailDigestEnabled ? '1' : '0'} />
                </div>
              </div>
              {emailDigestEnabled ? (
                <TimeField
                  id="digestHour"
                  name="digestHour"
                  label="Email delivery time"
                  value={digestHour}
                  onChange={setDigestHour}
                />
              ) : (
                <input type="hidden" name="digestHour" value={String(digestHour)} />
              )}
            </ChannelSubsection>

            <ChannelSubsection
              title="Slack digest"
              description={
                isSlackConnected
                  ? 'Deliver the same digest into your connected Slack channel.'
                  : 'Connect Slack from the dashboard to configure a Slack delivery hour.'
              }
            >
              {isSlackConnected ? (
                <TimeField
                  id="slackNotificationHour"
                  name="slackNotificationHour"
                  label="Slack delivery time"
                  value={slackNotificationHour}
                  onChange={setSlackNotificationHour}
                />
              ) : (
                <>
                  <p className="text-muted-foreground text-sm">Slack is not connected for this workspace yet.</p>
                  <input type="hidden" name="slackNotificationHour" value={String(slackNotificationHour)} />
                </>
              )}
            </ChannelSubsection>

            <ChannelSubsection
              title="Weekly team leaderboard"
              description="Optional Friday email/Slack ranking of lines changed for the week."
            >
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="weeklyLeaderboardEnabled">Enable weekly leaderboard</Label>
                <div className="flex items-center gap-2">
                  <Switch
                    id="weeklyLeaderboardEnabled"
                    checked={weeklyLeaderboardEnabled}
                    onCheckedChange={setWeeklyLeaderboardEnabled}
                  />
                  <input
                    type="hidden"
                    name="weeklyLeaderboardEnabled"
                    value={weeklyLeaderboardEnabled ? '1' : '0'}
                  />
                </div>
              </div>
              {weeklyLeaderboardEnabled ? (
                <TimeField
                  id="weeklyLeaderboardHour"
                  name="weeklyLeaderboardHour"
                  label="Friday delivery time"
                  value={weeklyLeaderboardHour}
                  onChange={setWeeklyLeaderboardHour}
                />
              ) : (
                <input type="hidden" name="weeklyLeaderboardHour" value={String(weeklyLeaderboardHour)} />
              )}
            </ChannelSubsection>
          </SettingsFormSection>

          <Button type="submit" disabled={processing || selectedRepos.length === 0}>
            {processing ? 'Saving…' : submitLabel}
          </Button>
        </div>
      )}
    </Form>
  )
}
